import apiClient from './api';
import { ApiResponse } from '../types/api';

// TokenSubs Payment Response interface
interface TokenSubsPaymentResponse {
  success: boolean;
  paymentUrl: string;
  orderId?: string;
  amount?: number;
  currency?: string;
}

// TokenSubs Payment Status interface
interface TokenSubsPaymentStatus {
  status: 'pending' | 'completed' | 'failed' | 'expired';
  transactionId?: string;
  blockchain?: string;
  txHash?: string;
  amount?: number;
  currency?: string;
}

// Cryptocurrency payment service for handling TokenSubs payments
export const cryptoPaymentService = {
  /**
   * Create a new TokenSubs payment session
   * @param subscriptionId - The subscription ID
   * @param amount - Payment amount
   * @param currency - Payment currency (THB or USD)
   * @param customerEmail - Optional customer email
   * @param successUrl - Optional URL to redirect after successful payment
   * @returns TokenSubs payment session with redirect URL
   */
  createPayment: async (
    subscriptionId: string,
    amount: number,
    currency: string,
    customerEmail?: string,
    successUrl?: string
  ): Promise<TokenSubsPaymentResponse> => {
    try {
      const response = await apiClient.post<ApiResponse<TokenSubsPaymentResponse>>('/payments/crypto/create', {
        subscription_id: subscriptionId,
        amount,
        currency: currency.toUpperCase(),
        customer_email: customerEmail,
        success_url: successUrl
      });
      
      if (!response.data || !response.data.data) {
        throw new Error('Failed to create crypto payment session');
      }
      
      return response.data.data;
    } catch (error) {
      console.error('Error creating cryptocurrency payment:', error);
      throw error;
    }
  },
  
  /**
   * Check the status of a TokenSubs payment
   * @param orderId - The order/subscription ID
   * @returns Current payment status
   */
  checkPaymentStatus: async (orderId: string): Promise<TokenSubsPaymentStatus> => {
    try {
      const response = await apiClient.get<ApiResponse<TokenSubsPaymentStatus>>(
        `/payments/crypto/status/${orderId}`
      );
      
      if (!response.data || !response.data.data) {
        throw new Error('Failed to get payment status');
      }
      
      return response.data.data;
    } catch (error) {
      console.error('Error checking cryptocurrency payment status:', error);
      throw error;
    }
  },
  
  /**
   * Get supported cryptocurrencies and networks
   * @returns List of supported cryptocurrencies and networks
   */
  getSupportedCurrencies: async (): Promise<{
    currencies: Array<{ code: string, name: string }>,
    networks: Array<{ id: string, name: string, chainId: number }>
  }> => {
    try {
      const response = await apiClient.get<ApiResponse<{
        currencies: Array<{ code: string, name: string }>,
        networks: Array<{ id: string, name: string, chainId: number }>
      }>>('/payments/crypto/supported');
      
      if (!response.data || !response.data.data) {
        throw new Error('Failed to get supported cryptocurrencies');
      }
      
      return response.data.data;
    } catch (error) {
      console.error('Error fetching supported cryptocurrencies:', error);
      // Return default supported currencies and networks
      return {
        currencies: [
          { code: 'USDC', name: 'USD Coin' },
          { code: 'USDT', name: 'Tether USD' }
        ],
        networks: [
          { id: 'ethereum', name: 'Ethereum (ETH)', chainId: 1 },
          { id: 'polygon', name: 'Polygon (MATIC)', chainId: 137 },
          { id: 'bsc', name: 'BNB Chain (BSC)', chainId: 56 }
        ]
      };
    }
  }
};

export default cryptoPaymentService; 